import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useCopiar } from '@common/hooks';
import { Alert, Button, Modal } from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { Check, Copy, Link, Mail } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEmailsDaSelecao, useInvalidarFase, useProgresso } from '../hooks';
import type { FiltroLote } from '../types/Painel';
import { EnviarConviteConteudo } from './EnviarConviteModal';
import { buscaDoFunil } from './funil';
import { linkDaCampanha } from './LinkCampanhaSecao';
import { TextoParaCopiar } from './TextoParaCopiar';

/** Limite de destinatários por mensagem do Exchange Online (R5). */
export const LIMITE_DESTINATARIOS = 500;
const SEPARADOR = '; '; // PE43
const DESTAQUE_MS = 2000;
/** Quem não respondeu (R1). */
const FILTRO: Partial<FiltroLote> = { situacao: 'elegivel', respondeu: ['nao'] };

export const selecaoDesatualizada = (e: unknown) =>
  isAxiosError(e) && e.response?.data?.codigo === 'SELECAO_DESATUALIZADA';

/** Os e-mails do bloco `i` (de 500), no formato que o Outlook aceita colar. */
export const blocoDeEmails = (emails: string[], i: number) =>
  emails.slice(i * LIMITE_DESTINATARIOS, (i + 1) * LIMITE_DESTINATARIOS);

type Props = {
  campanhaId: string;
  open: boolean;
  onFechar: () => void;
  /** A rodada de lembrete começou. */
  onEnviado?: () => void;
};

const CARTAO =
  'h-auto min-h-28 w-full flex-col items-center justify-center gap-1 whitespace-normal py-4';

/** Spec 31 R1 a R9: lembrar quem não respondeu por e-mail, pelos e-mails copiados ou pelo link. */
export function LembrarDialog({ campanhaId, open, onFechar, onEnviado }: Props) {
  const { t } = useTranslation(['convites', 'common', 'erros']);
  const navigate = useNavigate();
  const progresso = useProgresso(campanhaId).data;
  const invalidarFase = useInvalidarFase(campanhaId);
  const { copiar, copiado, manual } = useCopiar();
  const [enviando, setEnviando] = useState(false);
  const [destaque, setDestaque] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiados, setCopiados] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const n = progresso?.naoResponderam ?? 0;
  // E-mail e link só com a campanha recebendo respostas (R3, P29).
  const comCanais = progresso?.fase === 'agendada' || progresso?.fase === 'aberta';

  /** 409: alguém respondeu; o progresso refeito muda `n` e, com ele, a consulta (R8). */
  function destacar() {
    clearTimeout(timer.current);
    setDestaque(true);
    timer.current = setTimeout(() => setDestaque(false), DESTAQUE_MS);
    void invalidarFase();
  }
  // Já ao abrir, para os números dos cartões (R1).
  const emails = useEmailsDaSelecao(
    campanhaId,
    `lembrar:${n}`,
    { filtro: FILTRO, totalEsperado: n },
    open && n > 0,
    (e) => selecaoDesatualizada(e) && destacar(),
  );
  const dados = emails.data;
  const comEmail = dados ? dados.total - dados.semEmail : null;
  const blocos = dados ? Math.ceil(dados.emails.length / LIMITE_DESTINATARIOS) : 1;
  const erroAoAbrir =
    emails.isError && !selecaoDesatualizada(emails.error)
      ? t(`erros:${mensagemDeErro(emails.error)}`)
      : null;

  function fechar() {
    setEnviando(false);
    setErro(null);
    onFechar();
  }

  /** Busca de novo no clique (o público pode ter mudado, R8) e grava sem `await` antes (R5). */
  async function copiarEmails(bloco: number) {
    setErro(null);
    const texto = emails.buscarDeNovo().then((r) => {
      const parte = blocoDeEmails(r.emails, bloco);
      setCopiados(parte.length);
      return parte.join(SEPARADOR);
    });
    try {
      await copiar(`emails:${bloco}`, texto);
    } catch (e) {
      if (!selecaoDesatualizada(e)) setErro(t(`erros:${mensagemDeErro(e)}`));
    }
  }

  const conteudoCopiado = (rotulo: ReactNode) => (
    <>
      <Check aria-hidden className="size-5" />
      <span className="font-medium">{rotulo}</span>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={fechar}
      size={enviando ? '2xl' : 'lg'}
      ariaLabel={t('lembrar.copiarEmails')}
    >
      <Modal.Header
        title={
          enviando ? (
            t('dialogo.titulo')
          ) : (
            <span
              data-lembrar-titulo
              className={`rounded-sm px-1 transition-colors ${destaque ? 'bg-warning/20' : ''}`}
            >
              {t('lembrar.titulo', { count: n })}
            </span>
          )
        }
        onClose={fechar}
        dismissLabel={t('common:close')}
      />
      {enviando && comCanais && comEmail !== null ? (
        <EnviarConviteConteudo
          campanhaId={campanhaId}
          publico={comEmail}
          // Quem não tem e-mail fica de fora, para não gerar outra falha (R4).
          selecao={{ filtro: { ...FILTRO, temEmail: ['sim'] }, totalEsperado: comEmail }}
          onEnviado={onEnviado}
          onFechar={fechar}
          onCancelar={() => setEnviando(false)}
        />
      ) : (
        <Modal.Body>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {comCanais && (
                <Button
                  variant="primary"
                  className={CARTAO}
                  disabled={comEmail === null}
                  onClick={() => setEnviando(true)}
                >
                  <Mail aria-hidden className="size-5" />
                  <span className="font-medium">{t('lembrar.enviar')}</span>
                  {comEmail !== null && (
                    <span className="text-sm">{t('lembrar.pessoas', { count: comEmail })}</span>
                  )}
                </Button>
              )}
              {dados?.emails.length !== 0 && (
                <div className="flex flex-col gap-2">
                  {manual?.chave.startsWith('emails') ? (
                    <TextoParaCopiar texto={manual.texto} rotulo={t('lembrar.copiarEmails')} />
                  ) : blocos > 1 && dados ? (
                    <div className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-md border border-border p-3">
                      <Copy aria-hidden className="size-5" />
                      <span className="font-medium">{t('lembrar.copiarEmails')}</span>
                      <div className="flex flex-wrap justify-center gap-2">
                        {Array.from({ length: blocos }, (_, i) => {
                          const de = i * LIMITE_DESTINATARIOS + 1;
                          const ate = Math.min((i + 1) * LIMITE_DESTINATARIOS, dados.emails.length);
                          return (
                            <Button
                              key={de}
                              variant="secondary"
                              size="sm"
                              leftIcon={copiado === `emails:${i}` ? <Check size={14} /> : undefined}
                              onClick={() => void copiarEmails(i)}
                            >
                              {t('lembrar.bloco', { de, ate })}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      className={CARTAO}
                      onClick={() => void copiarEmails(0)}
                    >
                      {copiado === 'emails:0' ? (
                        conteudoCopiado(t('lembrar.copiados', { count: copiados }))
                      ) : (
                        <>
                          <Copy aria-hidden className="size-5" />
                          <span className="font-medium">{t('lembrar.copiarEmails')}</span>
                          {dados && <span className="text-sm">{dados.emails.length}</span>}
                        </>
                      )}
                    </Button>
                  )}
                  {(erro ?? erroAoAbrir) && (
                    <Alert variant="danger" role="alert">
                      {erro ?? erroAoAbrir}
                    </Alert>
                  )}
                </div>
              )}
              {comCanais &&
                (manual?.chave === 'link' ? (
                  <TextoParaCopiar texto={manual.texto} rotulo={t('lembrar.copiarLink')} />
                ) : (
                  <Button
                    variant="secondary"
                    className={CARTAO}
                    onClick={() => void copiar('link', linkDaCampanha(campanhaId))}
                  >
                    {copiado === 'link' ? (
                      conteudoCopiado(t('lembrar.linkCopiado'))
                    ) : (
                      <>
                        <Link aria-hidden className="size-5" />
                        <span className="font-medium">{t('lembrar.copiarLink')}</span>
                      </>
                    )}
                  </Button>
                ))}
            </div>
            {dados && dados.semEmail >= 1 && (
              <Button
                variant="secondary"
                size="sm"
                className="self-start"
                onClick={() => {
                  fechar();
                  // Quem a Luisa contata por outro canal (T 13:43).
                  navigate({
                    search: buscaDoFunil({ respondeu: 'nao', temEmail: 'nao' }),
                  });
                }}
              >
                {t('lembrar.semEmail', { count: dados.semEmail })}
              </Button>
            )}
          </div>
        </Modal.Body>
      )}
    </Modal>
  );
}
